import React, { useState, useEffect, useRef } from 'react'
import {
  CButton,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CSpinner,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilLockLocked } from '@coreui/icons'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeLowVision } from "@fortawesome/free-solid-svg-icons";
import GuestLayout from '@/layout/GuestLayout'
import { useFormik } from 'formik'
import validationHelper from "@/helpers/validationHelper";
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { loggedUser } from '@/store/features/userSlice'
import { useNavigate } from "react-router-dom";
import { UserService } from '@/api'
import { dashboard, signIn } from '@/libs/routes'
import { useTranslationContext } from '@/context/TranslationContext'
import { verify_otp } from '@/constants/translation/auth.translation'

/**
 * @name VerifyOtp
 *
 * @description
 * The VerifyOtp component allows users to verify the OTP sent to their email.
 * It takes the OTP and encodedToken from the forgot password flow and verifies them.
 * If successful, it redirects to the reset-password page with the resetToken.
 * 
 * API Response Format:
 * {
 *   message: "OTP verified",
 *   data: {
 *     resetToken: "0b89f5f1-2787-4480-8988-4907c51bf168"
 *   }
 * }
 *
 * @returns {JSX.Element} The JSX representation of the Verify OTP page.
 */
const VerifyOtp = () => {
  // Translation context to retrieve translated strings.
  const t = useTranslationContext();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [encodedToken, setEncodedToken] = useState('');
  const [email, setEmail] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [hasFailedAttempt, setHasFailedAttempt] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const submissionBlockRef = useRef(false);
  const countdownIntervalRef = useRef(null);

  // Retrieve logged-in user information from the Redux store.
  const checkLoggenInUser = useSelector(loggedUser);

  // Validation schema for the Verify OTP form using Formik.
  const validationSchema = validationHelper.verifyOtpValidation();

  // Formik hook for handling form state and submission.
  const verifyOtpForm = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema,
    onSubmit: (values) => {
      // Block any additional submissions immediately
      if (submissionBlockRef.current) {
        return;
      }
      submissionBlockRef.current = true;
      setIsSubmitting(true);
      doVerifyOtp(values);
    },
  });

  // Load stored data from localStorage on component mount
  useEffect(() => {
    const storedEncodedToken = localStorage.getItem('forgot_password_encoded_token');
    const storedEmail = localStorage.getItem('forgot_password_email');
        
    if (!storedEncodedToken) {
      toast.error(t(verify_otp.session_expired_error));
      navigate('/forgot-password');
      return;
    }
    
    setEncodedToken(storedEncodedToken);
    setEmail(storedEmail || '');
  }, [navigate, t]);

  // Redirects logged-in users to the dashboard to prevent access to the Verify OTP page.
  useEffect(() => {
    if (checkLoggenInUser && checkLoggenInUser.isLoggedIn) {
      navigate(dashboard);
    }
  }, [checkLoggenInUser, navigate]);

  // Cleanup countdown interval on component unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
    
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [countdown]);

  // Toggle OTP visibility
  const toggleOtpVisibility = () => setShowOtp(!showOtp);

  /**
   * The API call for verifying the OTP.
   * Sends the OTP and encodedToken to verify the OTP.
   * If successful, stores the resetToken and redirects to reset-password page.
   * If failed, blocks further attempts for 60 seconds.
   */
  const doVerifyOtp = async (values) => {
    try {
      const requestData = {
        encodedToken: encodedToken,
        otp: values.otp
      };
      
      
      const response = await UserService.verifyOtp(requestData);
      

      if (response.data?.resetToken) {
        // Store resetToken for the next step
        localStorage.setItem('forgot_password_reset_token', response.data.resetToken);
        toast.success(response.message || t(verify_otp.otp_verified_success));
        navigate('/reset-password');
      } else {
        toast.error(t(verify_otp.invalid_server_response));
      }
    } catch (error) {
      
      // Mark as failed attempt and block further attempts
      setHasFailedAttempt(true);
      
      // Clear the OTP input
      verifyOtpForm.setFieldValue('otp', '');
      
      // Don't start countdown automatically - let user click resend button
      // setCountdown(60);
      
      const message = error?.response?.data?.message || error?.message;
      toast.error(message);
    } finally {
      setIsSubmitting(false);
      submissionBlockRef.current = false;
    }
  };

  // Custom form submit handler with aggressive blocking
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Block any additional submissions immediately
    if (submissionBlockRef.current) {
      return;
    }
    
    // Validate and submit
    if (verifyOtpForm.isValid) {
      submissionBlockRef.current = true;
      setIsSubmitting(true);
      doVerifyOtp(verifyOtpForm.values);
    } else {
      // Trigger form validation
      verifyOtpForm.handleSubmit(e);
    }
  };

  const redirectToLogin = () => {
    navigate(signIn);
  };

  const resendOtp = async () => {
    try {
      if (!email) {
        toast.error(t(verify_otp.email_not_found_error));
        return;
      }

      if (countdown > 0) {
        toast.error(`${t(verify_otp.wait_before_resend_error).replace('{{countdown}}', countdown)}`);
        return;
      }

      setIsResending(true);
      
      const response = await UserService.resendForgotOtp({ email });
      
      // Update the encodedToken with the new one from the response
      const newEncodedToken = response.data?.encodedToken || response.encodedToken;
      if (newEncodedToken) {
        localStorage.setItem('forgot_password_encoded_token', newEncodedToken);
        setEncodedToken(newEncodedToken);
      }

      toast.success(response.message);
      
      // Start 60-second countdown
      setCountdown(60);
      
    } catch (error) {
      const message = error?.response?.data?.message || error?.message;
      toast.error(message);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <CForm className={`mt-4 ${isSubmitting ? 'pe-none opacity-50' : ''}`} onSubmit={handleSubmit}>
        <h1>{t(verify_otp.main_heading)}</h1>
        <p className="text-body-secondary">
          {t(verify_otp.sub_heading)}
          {email && <span className="d-block text-primary">{email}</span>}
        </p>
        
        <div className="mb-3">
          <CInputGroup>
            <CInputGroupText onClick={toggleOtpVisibility} style={{ cursor: 'pointer' }}>
              <FontAwesomeIcon icon={showOtp ? faEye : faEyeLowVision} />
            </CInputGroupText>
            <CFormInput
              placeholder={t(verify_otp.otp_placeholder)}
              autoComplete="one-time-code"
              type={showOtp ? "text" : "password"}
              name="otp"
              maxLength={6}
              className="form-control same-input-cls"
              onChange={verifyOtpForm.handleChange}
              onBlur={verifyOtpForm.handleBlur}
              value={verifyOtpForm.values.otp}
              feedbackInvalid={verifyOtpForm.errors.otp ? verifyOtpForm.errors.otp : null} />
          </CInputGroup>
          {/* Display error message if OTP is invalid */}
          {verifyOtpForm.errors.otp ? (
            <div className="error text-danger mt-1">{t(verifyOtpForm.errors.otp)}</div>
          ) : null}
        </div>

        {/* Submit and Cancel Buttons */}
        <div className="d-flex gap-2 mt-4">
          <CButton 
            color="primary" 
            className="px-4 flex-grow-1 d-flex gap-2 align-items-center justify-content-center" 
            type="submit" 
            disabled={isSubmitting}
          >
            {isSubmitting && <CSpinner size="sm" variant="grow" />}{t(verify_otp.verify_otp_btn_text)}
          </CButton>
          <CButton 
            color="secondary" 
            className="px-4 flex-grow-1" 
            type="button" 
            disabled={isSubmitting} 
            onClick={redirectToLogin}
          >
            {t(verify_otp.cancel_btn_text)}
          </CButton>
        </div>

        {/* Resend OTP Link */}
        <div className="text-center mt-3">
          <button 
            type="button" 
            className={`btn btn-link p-0 forgot-password-link ${countdown > 0 || isResending ? 'disabled' : ''}`}
            onClick={resendOtp}
            disabled={countdown > 0 || isResending}
          >
            {isResending ? (
              <>
                <CSpinner size="sm" variant="grow" className="me-1" />
                {t(verify_otp.sending_text)}
              </>
            ) : countdown > 0 ? (
              `${t(verify_otp.resend_otp_in_timer).replace('{{countdown}}', countdown)}`
            ) : (
              t(verify_otp.didnt_receive_otp_resend)
            )}
          </button>
        </div>
      </CForm>
    </>
  );
};

/**
 * @name getLayout
 *
 * @description
 * This function is used to wrap the VerifyOtp component with a `GuestLayout`. 
 * This layout is designed specifically for unauthenticated users and provides a consistent design for guest pages.
 * 
 * @param {JSX.Element} page - The current page (VerifyOtp).
 * @returns {JSX.Element} The page wrapped in a `GuestLayout`.
 */
VerifyOtp.getLayout = function getLayout(page) {
  return (
    <GuestLayout>{page}</GuestLayout>
  );
};

export default VerifyOtp;
